import React, { useContext, useEffect, useState } from "react";
import Image from "../../../Tags/Image";
import Flag from "../../../Flag";
import { getCountryFlag } from "../../../../constants/countries";
import SharedDataContext from "../../../../SharedDataContext";
import { useSmartQuery } from "../../../../apiHooks/useSmartQuery";
import { getActivityFilterCountries, getActivityFilterTimezones } from "../../../../apis/callActivity";
import { ChevronDown, Check, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../ui/popover";
import { Button } from "../../../ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../../ui/command";
import { cn } from "../../../../lib/utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { countries } from "countries-list";

dayjs.extend(utc);
dayjs.extend(timezone);
export default function BestTimesKPIs({
  data,
  selectedCountry,
  selectedTimezone,
  onCountryChange,
  onTimezoneChange,
  isLoading = false,
  error = null,
}) {
  const [countryOpen, setCountryOpen] = useState(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const staticData = {
    kpis: {
      total_calls: 420,
      total_calls_change_percent: 10,
      connect_rate_percent: 96,
      connect_rate_change_percent: 12,
      highest_pickup_time_slot: {
        time_range_utc: "02:00-04:00 UTC",
        start_hour_utc: 2,
        end_hour_utc: 4,
        connect_rate: 90,
        total_calls: 55,
      },
      lowest_pickup_time_slot: {
        time_range_utc: "22:00-00:00 UTC",
        start_hour_utc: 22,
        end_hour_utc: 24,
        connect_rate: 30,
        total_calls: 1,
      },
    },
  };

  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};

  const { data: countriesData } = useSmartQuery(
    ["activity_filter_countries", activeWorkspace?.id],
    () =>
      getActivityFilterCountries({
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
            "Content-Type": "application/json",
          },
        },
      }),
    {
      condition: !!activeWorkspace?.id,
    }
  );

  const { data: timezonesData } = useSmartQuery(
    ["activity_filter_timezones", activeWorkspace?.id, selectedCountry],
    () =>
      getActivityFilterTimezones({
        countryCode: selectedCountry || "US",
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
            "Content-Type": "application/json",
          },
        },
      }),
    {
      condition: !!activeWorkspace?.id && !!(selectedCountry || "US"),
    }
  );

  useEffect(() => {
    if (timezonesData?.data && timezonesData.data.length > 0) {
      const availableTimezoneIds = timezonesData.data;
      if (!availableTimezoneIds.includes(selectedTimezone)) {
        onTimezoneChange?.(availableTimezoneIds[0]);
      }
    }
  }, [timezonesData, selectedTimezone, onTimezoneChange]);

  const kpis = data?.kpis || staticData.kpis;

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse bg-gray-200 h-8 w-64 rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white dark:bg-[#181D27] border border-gray-200 rounded-lg p-5">
              <div className="animate-pulse">
                <div className="bg-gray-200 h-4 w-24 rounded mb-3"></div>
                <div className="bg-gray-200 h-8 w-16 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load best times data. Please try again.</p>
      </div>
    );
  }

  const getCountryName = (code) => {
    if (!code) return code;
    const countryData = countries[code.toUpperCase()];
    return countryData?.name || code;
  };

  const timezoneNames = {
    "America/Los_Angeles": "Pacific Time (PT)",
    "America/Denver": "Mountain Time (MT)",
    "America/Chicago": "Central Time (CT)",
    "America/New_York": "Eastern Time (ET)",
    "Europe/London": "Greenwich Mean Time (GMT)",
    "Europe/Paris": "Central European Time (CET)",
    "Asia/Kolkata": "India Standard Time (IST)",
    "Asia/Dubai": "Gulf Standard Time (GST)",
    "Australia/Sydney": "Australian Eastern Time (AET)",
  };

  const availableCountries = countriesData?.data || ["US", "IN"];
  const countriesMap = {};
  availableCountries.forEach((code) => {
    countriesMap[code] = getCountryName(code);
  });

  const availableTimezones = timezonesData?.data || ["America/Los_Angeles"];
  const timezonesMap = {};
  availableTimezones.forEach((tz) => {
    timezonesMap[tz] = timezoneNames[tz] || tz;
  });

  const renderFlag = (countryCode) => {
    if (!countryCode) return getCountryFlag("US");

    return <Flag code={countryCode} className="rounded-full" />;
  };

  const formatTimeRangeInTimezone = (startHourUtc, endHourUtc, timezoneId) => {
    if (startHourUtc === undefined || endHourUtc === undefined) {
      return "N/A";
    }

    try {
      const startDate = dayjs.utc().startOf("day").add(startHourUtc, "hour");
      const endDate = dayjs.utc().startOf("day").add(endHourUtc, "hour");

      const startFormatted = startDate.tz(timezoneId).format("h a");
      const endFormatted = endDate.tz(timezoneId).format("h a");

      const startParts = startFormatted.split(" ");
      const endParts = endFormatted.split(" ");

      if (startParts[1] === endParts[1]) {
        return `${startParts[0]}-${endParts[0]} ${endParts[1]}`;
      } else {
        return `${startFormatted}-${endFormatted}`;
      }
    } catch (error) {
      console.error("Error formatting time range:", error);
      return "N/A";
    }
  };

  return (
    <>
      <style>{`
        .dark .best-times-icon-gray img {
          filter: brightness(0) invert(53%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(100%) contrast(100%);
        }
      `}</style>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Popover open={countryOpen} onOpenChange={setCountryOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={countryOpen}
                className="justify-between h-10 rounded-lg border-gray-200 hover:border-gray-300 bg-white dark:bg-[#0C0E12] dark:border-gray-200 dark:hover:border-gray-300 dark:text-white"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg inline-flex items-center justify-center w-6 h-4 rounded-full overflow-hidden">
                    {renderFlag(selectedCountry || Object.keys(countriesMap)[0] || "US")}
                  </span>
                  <span className="text-sm text-[#414651] dark:text-white font-semibold">
                    {countriesMap[selectedCountry] ||
                      countriesMap[Object.keys(countriesMap)[0]] ||
                      "Select Country"}
                  </span>
                </div>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <div className="p-2">
                  <div className="border rounded-lg border-b-0 overflow-hidden">
                    <CommandInput placeholder="Search countries..." className="h-9" />
                  </div>
                </div>
                <CommandList>
                  <CommandEmpty>No country found.</CommandEmpty>
                  <CommandGroup>
                    {Object.entries(countriesMap).map(([code, name]) => (
                      <CommandItem
                        key={code}
                        value={name}
                        onSelect={() => {
                          onCountryChange?.(code);
                          setCountryOpen(false);
                        }}
                        className="min-h-[40px] flex items-center gap-2"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedCountry === code ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="text-lg inline-flex items-center justify-center w-6 h-4 rounded-full overflow-hidden">
                          {renderFlag(code)}
                        </span>
                        <span className="text-sm">{name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={timezoneOpen}
                className="justify-between h-10  rounded-lg border-gray-200 hover:border-gray-300 bg-white dark:bg-[#0C0E12] dark:border-gray-200 dark:hover:border-gray-300 dark:text-white"
              >
                <div className="flex items-center gap-2">
                  <Image
                    src="/images/icons/untitledui/call-activity/time-icon.svg"
                    alt="Timezone"
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-[#414651] dark:text-white font-semibold">
                    {timezonesMap[selectedTimezone] ||
                      timezonesMap[Object.keys(timezonesMap)[0]] ||
                      "Select Timezone"}
                  </span>
                </div>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="start">
              <Command>
                <div className="p-2">
                  <div className="border rounded-lg border-b-0 overflow-hidden">
                    <CommandInput placeholder="Search timezones..." className="h-9" />
                  </div>
                </div>
                <CommandList>
                  <CommandEmpty>No timezone found.</CommandEmpty>
                  <CommandGroup>
                    {Object.entries(timezonesMap).map(([code, name]) => (
                      <CommandItem
                        key={code}
                        value={name}
                        onSelect={() => {
                          onTimezoneChange?.(code);
                          setTimezoneOpen(false);
                        }}
                        className="min-h-[40px] flex items-center gap-2"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedTimezone === code ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <Image
                          src="/images/icons/untitledui/call-activity/time-icon.svg"
                          alt="Timezone"
                          className="w-4 h-4"
                        />
                        <span className="text-sm">{name}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* KPI Cards Row */}
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-white dark:bg-[#0C0E12] border border-gray-200 dark:border-[#22262F] rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <Image
                src="/images/icons/untitledui/call-activity/BT-totalcalls.svg"
                alt="Total calls"
                className="w-4 h-4 no-invert icon-gray-dark"
              />
              <span className="text-xs text-[#535862] dark:text-[#94979C] font-medium">
                Total calls per country
              </span>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-xl font-semibold text-[#181D27] dark:text-white">
                {kpis.total_calls || 0}
              </div>
              {(() => {
                const changePercent = kpis.total_calls_change_percent || 0;
                const isPositive = changePercent > 0;
                const isNegative = changePercent < 0;
                const arrowColor = isPositive
                  ? "text-[#067647]"
                  : isNegative
                    ? "text-[#B42318]"
                    : "text-[#414651]";

                return (
                  <div className="flex items-center gap-1 text-xs font-medium text-[#414651] dark:text-white border border-[#D5D7DA] dark:border-[#373A41] rounded-lg shadow-sm px-1 py-1">
                    {isPositive ? (
                      <span className={arrowColor}>↗</span>
                    ) : isNegative ? (
                      <span className={arrowColor}>↘</span>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 10l7-7m0 0l7 7m-7-7v18"
                        />
                      </svg>
                    )}
                    {kpis.total_calls_change_percent || 0}%
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="bg-white dark:bg-[#0C0E12] border border-gray-200 dark:border-[#22262F] rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <Image
                src="/images/icons/untitledui/call-activity/BT-avg-connectRate.svg"
                alt="Total calls"
                className="w-4 h-4 no-invert icon-gray-dark"
              />
              <span className="text-xs text-[#535862] dark:text-[#94979C] font-medium">
                Average connect rate
              </span>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-xl font-semibold text-[#181D27] dark:text-white">
                {Math.round(kpis.connect_rate_percent || 0)}%
              </div>
              {(() => {
                const changePercent = kpis.connect_rate_change_percent || 0;
                const isPositive = changePercent > 0;
                const isNegative = changePercent < 0;
                const arrowColor = isPositive
                  ? "text-[#067647]"
                  : isNegative
                    ? "text-[#B42318]"
                    : "text-[#414651]";

                return (
                  <div className="flex items-center gap-1 text-xs font-medium text-[#414651] dark:text-white border border-[#D5D7DA] dark:border-[#373A41] rounded-lg shadow-sm px-1 py-1">
                    {isPositive ? (
                      <span className={arrowColor}>↗</span>
                    ) : isNegative ? (
                      <span className={arrowColor}>↘</span>
                    ) : (
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 10l7-7m0 0l7 7m-7-7v18"
                        />
                      </svg>
                    )}
                    {kpis.connect_rate_change_percent || 0}%
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="bg-white dark:bg-[#0C0E12] border border-gray-200 dark:border-[#22262F] rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="best-times-icon-gray">
                <Image
                  src="/images/icons/untitledui/call-activity/BT-highest-pickup.svg"
                  alt="Total calls"
                  className="w-4 h-4 no-invert"
                />
              </div>
              <span className="text-xs text-[#535862] dark:text-[#94979C] font-medium">
                Highest pickup time slot
              </span>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-xl font-semibold text-[#181D27] dark:text-white">
                {kpis.highest_pickup_time_slot?.start_hour_utc !== undefined
                  ? formatTimeRangeInTimezone(
                      kpis.highest_pickup_time_slot.start_hour_utc,
                      kpis.highest_pickup_time_slot.end_hour_utc,
                      selectedTimezone || "America/New_York"
                    )
                  : "N/A"}
              </div>
              <div className="text-xs font-normal text-[#079455]">
                {kpis.highest_pickup_time_slot?.connect_rate || 0}% Connect rate
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-[#0C0E12] border border-gray-200 dark:border-[#22262F] rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="best-times-icon-gray">
                <Image
                  src="/images/icons/untitledui/call-activity/BT-highest-pickup.svg"
                  alt="Total calls"
                  className="w-4 h-4 no-invert"
                />
              </div>
              <span className="text-xs text-gray-600 dark:text-[#94979C]">Lowest pickup time slot</span>
            </div>
            <div className="flex items-end justify-between">
              <div className="text-xl font-semibold text-gray-900">
                {kpis.lowest_pickup_time_slot?.start_hour_utc !== undefined
                  ? formatTimeRangeInTimezone(
                      kpis.lowest_pickup_time_slot.start_hour_utc,
                      kpis.lowest_pickup_time_slot.end_hour_utc,
                      selectedTimezone || "America/New_York"
                    )
                  : "N/A"}
              </div>
              <div className=" text-xs font-medium text-[#F79009]">
                {kpis.lowest_pickup_time_slot?.connect_rate || 0}% Connect rate
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
